/**
 * AppSprites — die lebenden Wesen des StudoX Core.
 * ─────────────────────────────────────────────────────────────────────────────
 * 28 handgenerierte 6×6-Sheets (36 Frames, WebP mit Alpha) unter /sprites/.
 * `AppSprite` ist die Abspiel-Engine: rAF-Frame-Stepper über background-position,
 * lazy Preload (Bild lädt erst, wenn das Sprite gebraucht wird), sanfter
 * Fade-in sobald geladen, respektiert prefers-reduced-motion.
 *
 * Regel für Performance: gleichzeitig spielen immer nur 1–2 Sprites
 * (der Welt-Wächter + höchstens eine gehoverte Kachel).
 */
import { useEffect, useRef, useState } from "react";

export const SPRITES = {
  futurecity: { src: "/sprites/futurecity.webp", ratio: 0.6283 },
  step: { src: "/sprites/step.webp", ratio: 1.1173 },
  invite: { src: "/sprites/invite.webp", ratio: 1.0526 },
  clubs: { src: "/sprites/clubs.webp", ratio: 0.8656 },
  hub: { src: "/sprites/hub.webp", ratio: 0.9922 },
  invest: { src: "/sprites/invest.webp", ratio: 0.9094 },
  mic: { src: "/sprites/mic.webp", ratio: 0.85 },
  meet: { src: "/sprites/meet.webp", ratio: 1.0375 },
  cinema: { src: "/sprites/cinema.webp", ratio: 0.7783 },
  status: { src: "/sprites/status.webp", ratio: 0.8817 },
  adulting: { src: "/sprites/adulting.webp", ratio: 1.159 },
  horror: { src: "/sprites/horror.webp", ratio: 0.7944 },
  music: { src: "/sprites/music.webp", ratio: 0.8356 },
  impact: { src: "/sprites/impact.webp", ratio: 1.3034 },
  quest: { src: "/sprites/quest.webp", ratio: 0.9006 },
  lyrics: { src: "/sprites/lyrics.webp", ratio: 0.9144 },
  flow: { src: "/sprites/flow.webp", ratio: 0.75 },
  info: { src: "/sprites/info.webp", ratio: 0.9478 },
  rapvid: { src: "/sprites/rapvid.webp", ratio: 1.0 },
  create: { src: "/sprites/create.webp", ratio: 1.0256 },
  dossier: { src: "/sprites/dossier.webp", ratio: 0.7539 },
  help: { src: "/sprites/help.webp", ratio: 0.9561 },
  startup: { src: "/sprites/startup.webp", ratio: 0.9533 },
  discord: { src: "/sprites/discord.webp", ratio: 0.825 },
  events: { src: "/sprites/events.webp", ratio: 0.88 },
  phonk: { src: "/sprites/phonk.webp", ratio: 0.9339 },
  looksmax: { src: "/sprites/looksmax.webp", ratio: 0.7394 },
  support: { src: "/sprites/support.webp", ratio: 0.9567 },
};

/**
 * EMBLEMS — die runden Medaillon-Sprites (Ruhezustand jeder Gate-Kachel).
 * Gleiches 6×6-Format (Ausnahme: startup = 5×5). Dazu gibt es pro App ein
 * statisches Frame-0-Icon unter /sprites/icons/<app>.png für Mobile & Palette.
 */
export const EMBLEMS = {
  adulting: { src: "/sprites/emblems/adulting.webp", ratio: 1.0067 },
  cinema: { src: "/sprites/emblems/cinema.webp", ratio: 1.0482 },
  clubs: { src: "/sprites/emblems/clubs.webp", ratio: 1.0 },
  create: { src: "/sprites/emblems/create.webp", ratio: 0.9527 },
  discord: { src: "/sprites/emblems/discord.webp", ratio: 1.0 },
  dossier: { src: "/sprites/emblems/dossier.webp", ratio: 1.0 },
  events: { src: "/sprites/emblems/events.webp", ratio: 1.0807 },
  flow: { src: "/sprites/emblems/flow.webp", ratio: 1.0917 },
  futurecity: { src: "/sprites/emblems/futurecity.webp", ratio: 1.004 },
  help: { src: "/sprites/emblems/help.webp", ratio: 1.1477 },
  horror: { src: "/sprites/emblems/horror.webp", ratio: 1.0 },
  hub: { src: "/sprites/emblems/hub.webp", ratio: 0.996 },
  impact: { src: "/sprites/emblems/impact.webp", ratio: 1.0 },
  info: { src: "/sprites/emblems/info.webp", ratio: 1.0 },
  invest: { src: "/sprites/emblems/invest.webp", ratio: 0.9673 },
  invite: { src: "/sprites/emblems/invite.webp", ratio: 1.0676 },
  looksmax: { src: "/sprites/emblems/looksmax.webp", ratio: 0.8467 },
  lyrics: { src: "/sprites/emblems/lyrics.webp", ratio: 1.0877 },
  meet: { src: "/sprites/emblems/meet.webp", ratio: 1.0608 },
  mic: { src: "/sprites/emblems/mic.webp", ratio: 0.95 },
  music: { src: "/sprites/emblems/music.webp", ratio: 1.0 },
  phonk: { src: "/sprites/emblems/phonk.webp", ratio: 0.996 },
  quest: { src: "/sprites/emblems/quest.webp", ratio: 1.1416 },
  rapvid: { src: "/sprites/emblems/rapvid.webp", ratio: 0.672 },
  startup: { src: "/sprites/emblems/startup.webp", ratio: 1.0, cols: 5, frames: 25 },
  status: { src: "/sprites/emblems/status.webp", ratio: 1.0 },
  step: { src: "/sprites/emblems/step.webp", ratio: 1.004 },
  support: { src: "/sprites/emblems/support.webp", ratio: 0.996 },
};

const GRID = 6;
const FRAMES = 36;

/* module-level preload cache: a sheet is fetched exactly once */
const loadedSheets = new Set();
const pendingSheets = new Map(); // src -> Promise

function preload(src) {
  if (loadedSheets.has(src)) return Promise.resolve();
  if (pendingSheets.has(src)) return pendingSheets.get(src);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { loadedSheets.add(src); pendingSheets.delete(src); resolve(); };
    img.onerror = () => { pendingSheets.delete(src); resolve(); };
    img.src = src;
  });
  pendingSheets.set(src, p);
  return p;
}

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * <AppSprite appId size playing warm className style />
 *  · size    = Höhe in px (Breite folgt dem Sheet-Seitenverhältnis)
 *  · playing = Animation läuft (sonst Frame 0 als Standbild)
 *  · warm    = Bild schon laden, auch wenn noch nicht sichtbar/spielend
 */
export function AppSprite({ appId, variant = "char", size = 120, playing = false, warm = false, fps = 13, className = "", style = {} }) {
  const meta = (variant === "emblem" ? EMBLEMS : SPRITES)[appId];
  const [ready, setReady] = useState(meta ? loadedSheets.has(meta.src) : false);
  const [frame, setFrame] = useState(0);
  const raf = useRef(0);
  const noMotion = reducedMotion();

  // lazy preload — erst wenn das Sprite wirklich gebraucht wird
  useEffect(() => {
    if (!meta || ready || !(playing || warm)) return;
    let alive = true;
    preload(meta.src).then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [meta, ready, playing, warm]);

  // rAF frame stepper
  useEffect(() => {
    if (!meta || !ready || !playing || noMotion) return;
    const total = meta.frames || FRAMES;
    let last = 0;
    const step = (t) => {
      if (t - last >= 1000 / fps) { last = t; setFrame((f) => (f + 1) % total); }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [meta, ready, playing, fps, noMotion]);

  if (!meta) return null;
  const grid = meta.cols || GRID;
  const frames = meta.frames || FRAMES;
  const f = frame % frames;
  const col = f % grid;
  const row = Math.floor(f / grid);
  const w = Math.round(size * meta.ratio);

  return (
    <div
      className={className}
      aria-hidden
      style={{
        width: w,
        height: size,
        backgroundImage: ready ? `url(${meta.src})` : "none",
        backgroundSize: `${GRID * 100}% ${GRID * 100}%`,
        backgroundPosition: `${(col / (GRID - 1)) * 100}% ${(row / (GRID - 1)) * 100}%`,
        backgroundRepeat: "no-repeat",
        opacity: ready ? 1 : 0,
        transition: "opacity .45s ease",
        ...style,
      }}
    />
  );
}

export function hasSprite(appId) {
  return Boolean(SPRITES[appId]);
}

export function hasEmblem(appId) {
  return Boolean(EMBLEMS[appId]);
}

/* statisches Frame-0-Icon (Mobile-Grid & ⌘K-Palette) */
export function iconSrc(appId) {
  return EMBLEMS[appId] ? `/sprites/icons/${appId}.png` : null;
}
